// 二级单 OFFER_CONFIRM「权益/IP合作金额」表格（ProTablePlusForm 两层 addGroup 主从结构）。
// 数据模型（平铺）：每行 = 付款方式子行（level2，payment_method_key），携带主行字段（id/我方/对方主体/金额原币/汇率/谈判币金额，共享）。
//  主行 = level1 树节点（按 id 分组），非独立行；主行列（我方主体等）经 mergeRowSpanKey [group, id] 跨付款方式子行合并展示；
//  编辑主行列 → 更新 level1 节点（getData 读当前平铺数据 + treeInitData 同步重建，无 debounce），子行渲染继承，天然同步。
// 层级：root → [$insurance_group 常量节点] → [主行 id] → [付款方式 payment_method_key]（镜像 PaymentPlanTable item_key/payment_node_key/payment_type_key 范式）。
// 校验（validateFields 委托，父表单 watchChildFormValidate 提交时联动）：
//   1) 单元格必填（我方/对方主体、金额原币、汇率、付款类型、付款金额）
//   2) 结构：每个主行下至少一行真实付款方式子行（非 $addRow 占位）
//   3) 跨行：每主行 LF/MG 付款金额合计 = 该主行金额（原币）；Royalty 为百分比不参与
//   4) 跨行：全表 Royalty 占比合计 = 100%（仅 enable_royalty 勾选时；form 存小数 0.15=15%）
//   5) 保留原合计校验：各行金额（谈判币种）合计 = 预估总金额
// 联动：
//   - 付款类型可选项 = 费用预算勾选过滤（enable_license_fee/enable_min_guarantee/enable_royalty，镜像 PaymentPlanTable budgetConfig）；
//   - 付款金额列币种 = 金额（原币）列用户选的币种（original_amount.currency，锁定 Select）；
//   - 金额三列双向联动（原币×汇率→谈判币金额；谈判币金额÷汇率→原币；改币种自动带汇率）在**单元格级**（三列 editRender props.onChange）实现：
//     编辑控件 onChange 精确获知用户改了哪个字段（币种/金额/汇率），算好联动字段 → getData 读当前平铺数据 + treeInitData 同步重建 level1 节点（子行渲染继承）
//     + 写入 pendingLinkageRef 供 handleChange merge 透传父表单。弃用 treeSetRowById（其 updateTableData 走 useDebounceFn 400ms 才落盘，
//     滞后展示且污染跨轮基准，历史 bug：切换币种奇偶次只成功一次）。
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Decimal from 'decimal.js';
import { Button, Select } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import {
  ProTablePlusForm,
  type ProTablePlusFormRef,
} from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import { useDemandOptions } from '#features/useDemandOptions';
import { useChangeRate } from '../../../hooks/useChangeRate';
import { currencyEnum, toFiniteNumber } from '#utils/number';
import { getExchangeRate } from '../utils';
import CurrencyInput from '@mpc/common/src/components/SForm/SFormElement/CurrencyInput';
import SInputPercent from '@mpc/common/src/components/SForm/SFormElement/InputPercent';
import { v4 as uuidv4 } from 'uuid';
import { isNil } from 'lodash-es';

/** 主行允许删除的最小行数（仅 1 行时禁用删除，防止删空） */
const MIN_INSURANCE_ROWS = 1;

/** 付款类型（value 对齐 BudgetOfferTable BUDGET_ROWS item_key / 后端 IpCooperationPaymentPlanTypeEnum 小写） */
export const PAYMENT_TYPE_LF = 'lf';
export const PAYMENT_TYPE_MG = 'mg';
export const PAYMENT_TYPE_ROYALTY = 'royalty';

/** 付款类型选项（授权金 LF / 保底金 MG / 分成 Royalty） */
export const PAYMENT_TYPE_OPTIONS = [
  { value: PAYMENT_TYPE_LF, name: '授权金（LF）' },
  { value: PAYMENT_TYPE_MG, name: '保底金（MG）' },
  { value: PAYMENT_TYPE_ROYALTY, name: '分成（Royalty）' },
];

/** root addGroup 常量分组键（字段名 + 恒等值，作树根级父节点；$ 前缀虚拟字段不落接口，镜像 PaymentPlanTable item_key） */
const INSURANCE_GROUP_KEY = '$insurance_group';
const INSURANCE_GROUP_VALUE = 'ip_cooperation_details';

/** 联动计算：原币 × 汇率 → 谈判币种金额（原币/汇率齐备才返回，保留 2 位；汇率须 >0 防无效换算） */
function calcAmountByRate(
  originalAmount: unknown,
  rate: unknown
): number | undefined {
  if (
    originalAmount == null ||
    originalAmount === '' ||
    rate == null ||
    rate === '' ||
    Number(rate) <= 0
  ) {
    return undefined;
  }
  return Math.round(Number(originalAmount) * Number(rate) * 100) / 100;
}

/** 联动计算：金额 ÷ 汇率 → 原币金额（金额/汇率齐备才返回，保留 2 位；汇率须 >0 防除零） */
function calcOriginalByRate(
  amount: unknown,
  rate: unknown
): number | undefined {
  if (
    amount == null ||
    amount === '' ||
    rate == null ||
    rate === '' ||
    Number(rate) <= 0
  ) {
    return undefined;
  }
  return Math.round((Number(amount) / Number(rate)) * 100) / 100;
}

/** 金额（原币）取值：对象 { value, currency } → value；兼容历史数字型旧数据（回显未对象化兜底） */
function toOriginalAmountValue(
  originalAmount: InsuranceAmountRow['original_amount']
): number | string | undefined {
  if (
    typeof originalAmount === 'number' ||
    typeof originalAmount === 'string'
  ) {
    return originalAmount;
  }
  return originalAmount?.value;
}

/** 金额（谈判币种）取值：对象 { value, currency } → value；兼容历史数字型旧数据（回显未对象化兜底） */
function toAmountValue(
  amount: InsuranceAmountRow['amount']
): number | string | undefined {
  if (typeof amount === 'number' || typeof amount === 'string') {
    return amount;
  }
  return amount?.value;
}

/** 付款方式子行数据（对齐后端 IpCooperationAmountDetailDTO.paymentMethods / PaymentPlanItem.PaymentMethod） */
export interface InsurancePaymentMethodRow {
  /** 付款方式子行唯一标识（uuid，addGroup 树节点内部 id；每平铺行恒有，主行=level1 节点非独立行） */
  payment_method_key?: string;
  /** 付款类型（lf/mg/royalty，对齐后端 IpCooperationPaymentPlanTypeEnum 小写） */
  payment_type?: string;
  /** 付款金额（LF/MG=原币金额；ROYALTY=百分比小数 0.15=15%，对齐后端 amount） */
  payment_amount?: number;
}

/** 权益金额行（平铺，付款方式子行携带主行字段；主行 = level1 节点按 id 分组） */
export interface InsuranceAmountRow extends InsurancePaymentMethodRow {
  /** 主行唯一标识（uuid，addGroup 树节点 id；付款方式子行共享，合并主行列） */
  id?: string;
  our_party_id?: number | string;
  our_party_name?: string;
  other_party_id?: number | string;
  other_party_name?: string;
  /** 金额（谈判币种，即预估总金额币种；原币 × 汇率派生；CurrencyInput 对象化输出 { value, currency }） */
  amount?: { value?: number | string; currency?: string };
  /** 金额（原币），对象含金额 + 币种（币种权威源；对齐 CurrencyInput 值契约 { value, currency }） */
  original_amount?: { value?: number | string; currency?: string };
  /** 汇率（原币 → 谈判币种，选币种自动带入，同币种锁定 1，可手动修改） */
  exchange_rate?: number;
  /** 谈判币种（= 预估总金额币种，虚拟字段，不落接口） */
  $negotiation_currency?: string;
  /** root addGroup 常量分组键（镜像 PaymentPlanTable item_key；$ 前缀虚拟字段不落接口） */
  $insurance_group?: string;
  /** addGroup 占位行标记 */
  $addRow?: string[];
}

interface InsuranceAmountTableProps {
  /** 受控 rows（父表单 ip_cooperation_details 字段值） */
  value?: InsuranceAmountRow[];
  onChange?: (rows: InsuranceAmountRow[]) => void;
  /** 只读态（详情非编辑） */
  view?: boolean;
  /** 我方主体选项（数据源待后端契约，先经 props 注入） */
  ourPartyOptions?: { value: string | number; name: string }[];
  /** 谈判币种（= 预估总金额币种，来自 formData.currency；原币 == 谈判币种时汇率锁定 1 不可改） */
  negotiationCurrency?: string;
  /** 预估总金额（各行「金额（谈判币种）」合计须等于此值，用 decimal.js 校验） */
  estimatedTotalAmount?: number;
  /** 费用预算勾选配置（付款类型可选项过滤：enable_license_fee/enable_min_guarantee/enable_royalty，镜像 PaymentPlanTable budgetConfig） */
  paymentBudgetConfig?: {
    lfEnabled?: boolean;
    mgEnabled?: boolean;
    royaltyEnabled?: boolean;
  };
}

/** 父表单 watchChildFormValidate 提交时调用的校验能力（镜像 EffectEvaluationTable/PaymentPlanTable） */
export interface InsuranceAmountTableRef {
  validateFields: () => Promise<unknown>;
}

/** 主体选项：字典选项 + 已保存行主体合并去重（保证已选主体在编辑/回显时仍可选） */
function mergePartyOptions(
  options: { value: string | number; name: string }[],
  rows?: InsuranceAmountRow[],
  keys: {
    id: 'our_party_id' | 'other_party_id';
    name: 'our_party_name' | 'other_party_name';
  } = { id: 'our_party_id', name: 'our_party_name' }
): { value: string | number; name: string }[] {
  const fromRows = (rows ?? [])
    .map((r) => ({ value: r[keys.id], name: r[keys.name] }))
    .filter((p) => p.value != null && p.name);
  // 平铺行中同一主行的主体字段被多条付款方式子行携带重复 → 先对 fromRows 自身按 value 去重
  const uniqueFromRows = fromRows.filter(
    (p, i) =>
      fromRows.findIndex((q) => q.value?.toString() === p.value?.toString()) ===
      i
  );
  return [
    ...uniqueFromRows.filter(
      (p) => !options.some((o) => o.value?.toString() === p.value?.toString())
    ),
    ...options,
  ];
}

/** 付款金额编辑控件：LF/MG → CurrencyInput（币种 = 主行金额原币币种，锁定只读）；Royalty → SInputPercent（小数 0.15=15%） */
const PaymentAmountInput: React.FC<{
  value?: number;
  onChange?: (v?: number) => void;
  row?: InsuranceAmountRow;
  editable?: boolean;
}> = ({ value, onChange, row, editable = true }) => {
  const isRoyalty = row?.payment_type === PAYMENT_TYPE_ROYALTY;
  // 只读态：LF/MG → CurrencyInput view（金额 + 原币币种），Royalty → 百分比文本
  if (!editable) {
    if (isRoyalty) {
      if (value == null) return <span>--</span>;
      return (
        <span>
          {new Decimal(value)
            .mul(100)
            .toFixed(2)
            .replace(/\.?0+$/, '')}
          %
        </span>
      );
    }
    return (
      <CurrencyInput
        view
        value={{
          value: value ?? '',
          currency: row?.original_amount?.currency,
        }}
      />
    );
  }
  if (isRoyalty) {
    return (
      <SInputPercent
        value={value}
        onChange={(v) => {
          onChange?.(v == null ? undefined : Number(v));
        }}
        min={0}
        max={1}
        digits={2}
        valueType="number"
        placeholder="请输入"
        style={{ width: '100%' }}
      />
    );
  }
  return (
    <CurrencyInput
      value={{
        value,
        currency: row?.original_amount?.currency,
      }}
      onChange={(next) => {
        const v = next?.value;
        onChange?.(v == null || v === '' ? undefined : Number(v));
      }}
      precision={2}
      showCurrency
      showUnitIndicator
      // 币种 = 金额（原币）列用户选的币种（original_amount.currency，Select 锁定不可改）
      selectProps={{ disabled: true }}
      inputProps={{ min: 0.01, placeholder: '请输入' }}
    />
  );
};

/** 我方主体/对方主体 Select（只读态纯文本展示 name 快照） */
const PartySelect: React.FC<{
  value?: number | string;
  onChange?: (v?: number | string) => void;
  row?: InsuranceAmountRow;
  editable?: boolean;
  nameKey: 'our_party_name' | 'other_party_name';
  options?: { value: string | number; name: string }[];
  onSearch?: (keyword: string) => void;
}> = ({
  value,
  onChange,
  row,
  editable = true,
  nameKey,
  options = [],
  onSearch,
}) => {
  if (!editable) {
    const name = row?.[nameKey];
    return <span>{name || value || '--'}</span>;
  }
  return (
    <Select
      value={value}
      onChange={(v) => onChange?.(v)}
      options={options}
      showSearch
      allowClear
      filterOption={false}
      onSearch={onSearch}
      placeholder={
        nameKey === 'our_party_name' ? '请选择' : '请输入供应商名称搜索'
      }
      style={{ width: '300px' }}
    />
  );
};

const InsuranceAmountTable = forwardRef<
  InsuranceAmountTableRef,
  InsuranceAmountTableProps
>(
  (
    {
      value,
      onChange,
      view = false,
      ourPartyOptions = [],
      negotiationCurrency,
      estimatedTotalAmount,
      paymentBudgetConfig,
    },
    ref
  ) => {
    const tableRef = useRef<ProTablePlusFormRef>(null);
    const isEdit = !view;
    // 最新受控 value 引用：校验闭包读最新行，避免把 value 加入 columns 依赖导致每次编辑重建列
    const valueRef = useRef<InsuranceAmountRow[]>(value ?? []);
    valueRef.current = value ?? [];

    // 汇率表（选择原币币种自动带入汇率，useChangeRate 模块级单例缓存 + 并发控制）
    const { changeRateList } = useChangeRate();
    // 对方主体远程搜索：useDemandOptions 按 ip_cooperation_other_party 字典 key + keyword 搜索
    const [otherPartyKeyword, setOtherPartyKeyword] = useState('');
    const { optionsListMap, getOptionsMap } = useDemandOptions({
      keys: ['ip_cooperation_other_party'],
      params: otherPartyKeyword
        ? { ip_cooperation_other_party_keyword: otherPartyKeyword }
        : undefined,
      manual: true,
      cache: false,
    });
    const selectedRef = useRef<Record<string, any>>({});
    // 我方主体选项：字典选项 + 已保存行主体合并去重（保证已选主体在编辑/回显时仍可选）
    const mergedOurPartyOptions = useMemo(
      () => mergePartyOptions(ourPartyOptions, valueRef.current),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ourPartyOptions]
    );
    // 对方主体搜索 options：远程搜索结果 + 已保存行对方主体合并（detail 回显行值可选项，镜像 mergedOurPartyOptions）+ 选中项兜底合并（避免 Select label 丢失）
    const otherPartyOptions = useMemo(() => {
      const list = optionsListMap?.['ip_cooperation_other_party'] ?? [];
      // detail 回显/已保存行对方主体合并（无 keyword 时也须保留，保证回显值可选）
      const fromRows = mergePartyOptions([], valueRef.current, {
        id: 'other_party_id',
        name: 'other_party_name',
      });
      const selected = Object.values(selectedRef.current).filter(Boolean);
      const base = selected.filter(
        (s: any) =>
          !fromRows.some((o: any) => o.value === s.value) &&
          !list.some((o: any) => o.value === s.value)
      );
      if (!otherPartyKeyword) return [...base, ...fromRows];
      return [
        ...base,
        ...fromRows.filter(
          (o: any) => !list.some((l: any) => l.value === o.value)
        ),
        ...list,
      ];
    }, [otherPartyKeyword, optionsListMap, value]);

    // 按主行 id 分组的工具（$addRow 占位行也参与分组——携带同 id 主行字段快照，联动写回时一并同步；同 id 行共享主行字段，取首行作代表）
    const groupByMainId = (rows: InsuranceAmountRow[]) => {
      const map = new Map<string, InsuranceAmountRow[]>();
      (rows ?? [])
        .filter((r) => r.id)
        .forEach((r) => {
          const list = map.get(r.id as string) ?? [];
          list.push(r);
          map.set(r.id as string, list);
        });
      return map;
    };

    // 同步更新主行（level1）字段：读当前表格平铺数据 → 同 id 行合并 nextFields → treeInitData 重建树。
    // 替代 treeSetRowById（其 updateTableData 走 useDebounceFn 400ms 才落盘，会滞后联动展示，历史 bug 根源）；
    // 直接编辑字段须随 nextFields 一并传入（getData 此刻仍为渲染中旧值，仅传联动字段会导致直接编辑字段回退）。
    const updateMainRowFields = useMemoizedFn(
      (mainId: string, nextFields: Partial<InsuranceAmountRow>) => {
        const current = tableRef.current?.getData?.() ?? [];
        const updated = current.map((r: InsuranceAmountRow) =>
          r.id === mainId ? { ...r, ...nextFields } : r
        );
        tableRef.current?.treeInitData?.(updated);
      }
    );

    // 暴露校验能力：委托内部 ProTablePlusForm.validateFieldsAndScroll（镜像 EffectEvaluationTable/PaymentPlanTable）
    useImperativeHandle(
      ref,
      () => ({
        validateFields: () => {
          // 1) 单元格必填校验（我方/对方主体、金额原币、汇率、付款类型、付款金额）委托内部表格
          const cellValidate = tableRef.current?.validateFieldsAndScroll
            ? tableRef.current.validateFieldsAndScroll()
            : Promise.resolve();
          return cellValidate
            .then(() => {
              const rows = (valueRef.current ?? []).filter(
                (item) =>
                  !(
                    item.$addRow &&
                    item.$addRowParentId === INSURANCE_GROUP_VALUE
                  )
              );
              // 主行索引（按 id 出现顺序编号，供报错定位行）
              const mainOrder: Record<string, number> = {};
              let order = 0;
              rows.forEach((r) => {
                if (r.id && mainOrder[r.id] == null) mainOrder[r.id] = ++order;
              });
              const groups = groupByMainId(rows);
              // 1.5) 结构校验：至少一行 group 主行（无主行 → 无可分配权益，直接拦截）
              if (groups.size === 0) {
                return Promise.reject('请至少添加一行');
              }
              // 1.6) 主行主体必填校验：我方/对方主体为自定义 PartySelect 组件，editRender 未配 rule 必填，
              //      内部表格 validateFieldsAndScroll 不会拦截，须在此显式校验（取组内首行作代表，镜像 Step 3）
              for (const [mainId, groupRows] of groups.entries()) {
                const rep = groupRows[0];
                if (rep?.our_party_id == null) {
                  return Promise.reject(
                    `第 ${mainOrder[mainId] ?? '?'} 个权益下请选择我方主体`
                  );
                }
                if (rep?.other_party_id == null) {
                  return Promise.reject(
                    `第 ${mainOrder[mainId] ?? '?'} 个权益下请选择对方主体`
                  );
                }
              }
              // 2) 结构校验：每个主行下至少一行真实付款方式子行（平铺行即付款方式子行；空组不进入此处）
              //    （主行 = level1 节点，无独立行；有真实子行即满足）
              for (const mainId of groups.keys()) {
                const hasChild = rows.some(
                  (r) => r.id === mainId && r.payment_method_key
                );
                if (!hasChild) {
                  return Promise.reject(`每个权益下请至少添加一行付款方式`);
                }
              }
              // 3) 跨行汇总校验：每主行 LF/MG 付款金额合计 = 该主行金额（原币）；Royalty 为百分比不参与
              for (const [mainId, groupRows] of groups.entries()) {
                const rep = groupRows[0];
                const sum = groupRows
                  .filter((r) => !r.$addRow)
                  .filter((r) => r.payment_type !== PAYMENT_TYPE_ROYALTY)
                  .reduce(
                    (acc, r) => acc.plus(Number(r.payment_amount) || 0),
                    new Decimal(0)
                  );
                const original = new Decimal(
                  toFiniteNumber(toOriginalAmountValue(rep?.original_amount))
                ).toDecimalPlaces(2);
                if (!sum.toDecimalPlaces(2).eq(original)) {
                  return Promise.reject(
                    `第 ${mainOrder[mainId] ?? '?'} 个权益下授权金/保底金付款金额合计须等于金额（原币）`
                  );
                }
              }
              // 4) 跨行汇总校验：全表 Royalty 占比合计须等于 100%（仅 enable_royalty 勾选时；
              //    form 存小数 0.15=15%，合计需为 1；全表跨所有主行汇总，不带行定位）
              if (paymentBudgetConfig?.royaltyEnabled) {
                const royaltySum = rows
                  .filter((r) => !r.$addRow)
                  .filter((r) => r.payment_type === PAYMENT_TYPE_ROYALTY)
                  .reduce(
                    (acc, r) => acc.plus(Number(r.payment_amount) || 0),
                    new Decimal(0)
                  );
                if (!royaltySum.eq(1)) {
                  return Promise.reject('分成（Royalty）占比合计需为100%');
                }
              }
              // 5) 保留原合计校验：各行金额（谈判币种）合计须等于预估总金额（每主行计一次，避免子行重复计数）
              const amountSum = Array.from(groups.values()).reduce(
                (acc, groupRows) =>
                  acc.plus(toAmountValue(groupRows[0]?.amount) ?? 0),
                new Decimal(0)
              );
              const total = new Decimal(
                toFiniteNumber(estimatedTotalAmount)
              ).toDecimalPlaces(2);
              if (!amountSum.toDecimalPlaces(2).eq(total)) {
                return Promise.reject(
                  '各权益金额（谈判币种）合计须等于报价总金额'
                );
              }
              return Promise.resolve();
            })
            .catch((error: any) => {
              return Promise.reject(error?.message ?? error);
            });
        },
      }),
      [estimatedTotalAmount, paymentBudgetConfig]
    );

    const { run: handleSearch } = useDebounceFn(
      (keyword: string) => {
        setOtherPartyKeyword(keyword || '');
        if (keyword) getOptionsMap();
      },
      { wait: 300 }
    );

    /** 新增主行（权益金额分配）：treeAddToParent 挂到 root 常量分组节点（level0）；付款方式占位由 addGroupTable 自动补 */
    const handleAddMainRow = useMemoizedFn(() => {
      tableRef.current?.treeAddToParent(
        'id',
        {
          id: uuidv4(),
          [INSURANCE_GROUP_KEY]: INSURANCE_GROUP_VALUE,
          amount: { currency: negotiationCurrency },
          original_amount: {},
        },
        INSURANCE_GROUP_VALUE
      );
    });

    /** 新增付款方式子行（挂到当前主行 id 下） */
    const handleAddPaymentMethod = useMemoizedFn(
      (mainRow: InsuranceAmountRow) => {
        tableRef.current?.treeAddToParent(
          'payment_method_key',
          {
            payment_method_key: uuidv4(),
            id: mainRow?.id,
            [INSURANCE_GROUP_KEY]: mainRow?.[INSURANCE_GROUP_KEY],
          },
          mainRow?.id
        );
      }
    );

    /** 删除整个主行（含全部付款方式子行；treeDeleteRowById 按 level1 节点 id 删除节点及子节点） */
    const handleDeleteMainRow = useMemoizedFn((id?: string) => {
      if (!id) return;
      tableRef.current?.treeDeleteRowById(id);
    });

    /** 删除付款方式子行 */
    const handleDeletePaymentMethod = useMemoizedFn((key?: string) => {
      if (!key) return;
      tableRef.current?.treeDeleteRowById(key);
    });

    /** 受控 onChange 包装：金额三列联动已由单元格级 editRender 的 getData+treeInitData 同步重建透传（子行渲染继承 level1 字段，天然同步），
     *  此处 rows 即重建后的最新平铺数据（含联动字段）；子行编辑（payment_type/payment_amount）不触发金额联动。 */
    const handleChange = useMemoizedFn(
      (rows: InsuranceAmountRow[], _changeData) => {
        // 1) 我方/对方主体 name 快照补齐（Select 只回传 value，经 options 反查补 name；id 清空同步清空 name）
        const enriched = (rows ?? []).map((row) => {
          const ourOpt = mergedOurPartyOptions?.find(
            (o) => o.value?.toString() === row?.our_party_id?.toString()
          );
          let otherOpt: { value: string | number; name?: string } | undefined;
          if (row?.other_party_id != null) {
            otherOpt = otherPartyOptions?.find(
              (o) => o.value?.toString() === row?.other_party_id?.toString()
            );
            if (otherOpt) selectedRef.current[row?.id ?? ''] = otherOpt;
          }
          return {
            ...row,
            our_party_name:
              row?.our_party_id == null
                ? undefined
                : (ourOpt?.name ?? row?.our_party_name),
            other_party_name:
              row?.other_party_id == null
                ? undefined
                : (otherOpt?.name ?? row?.other_party_name),
          };
        });

        onChange?.(enriched);
      }
    );

    // 付款类型可选项：费用预算勾选过滤（未勾选不出现）；行自身已选类型兜底补齐（费用预算取消勾选后该行仍可回显）
    const getPaymentTypeOptions = useMemoizedFn((row?: InsuranceAmountRow) => {
      const enabled = new Set<string>();
      if (!paymentBudgetConfig || paymentBudgetConfig.lfEnabled)
        enabled.add(PAYMENT_TYPE_LF);
      if (!paymentBudgetConfig || paymentBudgetConfig.mgEnabled)
        enabled.add(PAYMENT_TYPE_MG);
      if (!paymentBudgetConfig || paymentBudgetConfig.royaltyEnabled)
        enabled.add(PAYMENT_TYPE_ROYALTY);
      const base = PAYMENT_TYPE_OPTIONS.filter((o) => enabled.has(o.value));
      // 行自身已选类型兜底（下拉仍可回显）
      if (row?.payment_type && !enabled.has(row.payment_type)) {
        const opt = PAYMENT_TYPE_OPTIONS.find(
          (o) => o.value === row.payment_type
        );
        if (opt) return [...base, opt];
      }
      return base;
    });

    // 谈判币种名（列标题「金额（美元）」；用项目 currencyEnum 小写键映射）
    const negotiationCurrencyName = useMemo(() => {
      const key = negotiationCurrency?.toLowerCase();
      return key ? (currencyEnum[key] ?? key) : '';
    }, [negotiationCurrency]);

    const columns = useMemo<AddGroupFormColumn[]>(
      () => {
        // 主行操作列（删除整个主行；mergeRowSpanKey [group,id] 跨付款方式子行合并，仅首行渲染）
        const mainOperationColumn: AddGroupFormColumn = {
          dataIndex: 'insurance_main_operation',
          title: '操作',
          width: 60,
          mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id'],
          render: (__v: any, row: InsuranceAmountRow) => {
            // $addRow 占位行不渲染删除
            // if (row?.$addRow) return null;
            return (
              <Button
                danger
                type="text"
                onClick={() => handleDeleteMainRow(row?.id)}
              >
                删除
              </Button>
            );
          },
        };

        // 主行列（mergeRowSpanKey [group,id] 跨付款方式子行合并展示；编辑更新 level1 节点由子行继承）
        const mainColumns: AddGroupFormColumn[] = [
          {
            dataIndex: 'our_party_id',
            title: '我方主体',
            width: 200,
            mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id'],
            editRender: ({ row }) => ({
              name: 'custom-component',
              editable: isEdit,
              props: {
                customComp: PartySelect,
                row,
                editable: isEdit,
                nameKey: 'our_party_name' as const,
                options: mergedOurPartyOptions,
              },
            }),
            render: (v: any, row: InsuranceAmountRow) => (
              <PartySelect
                value={v}
                row={row}
                editable={false}
                nameKey="our_party_name"
              />
            ),
          },
          {
            dataIndex: 'other_party_id',
            title: '对方主体',
            width: 240,
            mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id'],
            editRender: ({ row }) => ({
              name: 'custom-component',
              editable: isEdit,
              props: {
                customComp: PartySelect,
                row,
                editable: isEdit,
                nameKey: 'other_party_name' as const,
                options: otherPartyOptions,
                onSearch: handleSearch,
              },
            }),
            render: (v: any, row: InsuranceAmountRow) => (
              <PartySelect
                value={v}
                row={row}
                editable={false}
                nameKey="other_party_name"
              />
            ),
          },
          {
            dataIndex: 'original_amount',
            title: '金额（原币）',
            width: 260,
            mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id'],
            editRender: ({ row }) => ({
              name: 'currencyInput',
              editable: isEdit,
              props: {
                precision: 2,
                min: 0,
                showCurrency: true,
                showUnitIndicator: true,
                onCurrencyChange: (newCurrency) => {
                  const mainId = row?.id;
                  if (!mainId || !isEdit) return;
                  const amountCurrency =
                    row?.amount?.currency || negotiationCurrency;
                  // 改原币币种：币种权威源（original_amount.currency）变化 → 查汇率带入 exchange_rate + 重算 amount
                  const rate = getExchangeRate(
                    changeRateList,
                    newCurrency,
                    amountCurrency
                  );
                  let nextFields = null;
                  if (rate != null) {
                    nextFields = {
                      exchange_rate: rate,
                      amount: {
                        currency: amountCurrency,
                        value: calcAmountByRate(
                          row?.original_amount?.value,
                          rate
                        ),
                      },
                    };
                  } else {
                    // 查不到汇率：清空汇率交用户手填（汇率列必填校验提示），amount 保留待汇率分支重算
                    nextFields = { exchange_rate: undefined };
                  }

                  if (nextFields) {
                    updateMainRowFields(mainId, {
                      original_amount: {
                        ...(row?.original_amount || {}),
                        currency: newCurrency,
                      },
                      ...nextFields,
                    });
                  }
                },
                // 单元格级联动（币种/金额）：在编辑控件 onChange 精确获知改了哪个字段，getData+treeInitData 同步重建写回主行节点（子行渲染继承）
                // （不依赖表格级 prevRowsRef diff；弃用 treeSetRowById —— 其 updateTableData debounce 400ms 会污染跨轮基准，历史 bug：切换币种奇偶次只成功一次）
                onChange: (e: any, r: InsuranceAmountRow) => {
                  const mainId = r?.id;
                  if (!mainId || !isEdit) return;

                  const amountCurrency =
                    row?.amount?.currency || negotiationCurrency;
                  const newOriginalValue = e?.value;
                  let nextFields: Partial<InsuranceAmountRow> | null = null;

                  // 仅原币金额值变化 → 重算 amount（原币 × 当前汇率）
                  const next = calcAmountByRate(
                    newOriginalValue,
                    row?.exchange_rate
                  );
                  if (next != null)
                    nextFields = {
                      amount: {
                        currency: amountCurrency,
                        value: next,
                      },
                    };
                  if (nextFields) {
                    updateMainRowFields(mainId, nextFields);
                  }
                },
              },
              rule: [
                {
                  validator: (rule, v) => {
                    if (
                      !v ||
                      isNil(v?.value) ||
                      isNil(v?.currency) ||
                      v?.value === ''
                    ) {
                      return Promise.reject(new Error('请填写金额（原币）'));
                    }
                    if (v?.value === 0) {
                      return Promise.reject(new Error('金额（原币）不能为0'));
                    }
                    return Promise.resolve();
                  },
                },
              ],
            }),
            render: (v: any, row: InsuranceAmountRow) => (
              <CurrencyInput view value={v} />
            ),
          },
          {
            dataIndex: 'exchange_rate',
            title: '汇率',
            width: 150,
            mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id'],
            editRender: ({ row }) => {
              const rep = valueRef.current.find((r) => r.id === row?.id);
              return {
                name: 'MPCInputNumber',
                editable: isEdit,
                props: {
                  size: 'middle',
                  min: 0,
                  precision: 4,
                  toFixed: 4,
                  style: { width: '100%' },
                  direction: 'vertical',
                  // 原币 == 谈判币种时汇率锁定 1 且不可修改；否则可编辑（原币→谈判币种汇率）
                  disabled:
                    rep?.original_amount?.currency != null &&
                    negotiationCurrency != null &&
                    String(rep.original_amount.currency).toLowerCase() ===
                      String(negotiationCurrency).toLowerCase(),
                  // 单元格级联动（汇率）：改汇率 → 重算 amount（原币 × 新汇率）
                  onChange: (e: any, r: InsuranceAmountRow) => {
                    const v = e?.target?.value;
                    const mainId = row?.id;
                    if (!mainId || !isEdit) return;

                    const amountCurrency =
                      row?.amount?.currency || negotiationCurrency;
                    const next = calcAmountByRate(
                      row?.original_amount?.value,
                      v
                    );
                    console.log(222222, next);
                    if (next != null) {
                      const nextFields: Partial<InsuranceAmountRow> = {
                        amount: { currency: amountCurrency, value: next },
                      };
                      updateMainRowFields(mainId, {
                        exchange_rate: v,
                        ...nextFields,
                      });
                    }
                  },
                },
                rule: [{ required: true, message: '请填写汇率' }],
              };
            },
            render: (v: any) => {
              if (v == null || Number.isNaN(Number(v))) return '--';
              return Number(v).toFixed(4);
            },
          },
          {
            dataIndex: 'amount',
            title: `金额（${negotiationCurrencyName || '谈判币种'}）`,
            width: 260,
            mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id'],
            editRender: ({ row }) => ({
              name: 'currencyInput',
              editable: isEdit,
              props: {
                precision: 2,
                min: 0,
                showCurrency: true,
                showUnitIndicator: true,
                selectProps: { disabled: true },
                // 单元格级联动（谈判币金额）：改 amount → 反算原币金额（谈判币 ÷ 汇率）
                onChange: (e: any, r: InsuranceAmountRow) => {
                  const mainId = r?.id;
                  if (!mainId || !isEdit) return;
                  const prevRow =
                    valueRef.current.find((it) => it.id === mainId) ?? r;
                  const originalCurrency = prevRow?.original_amount?.currency;
                  const next = calcOriginalByRate(
                    e?.value,
                    prevRow?.exchange_rate
                  );
                  if (next != null) {
                    const nextFields: Partial<InsuranceAmountRow> = {
                      original_amount: {
                        value: next,
                        currency: originalCurrency,
                      },
                    };
                    updateMainRowFields(mainId, {
                      amount: e,
                      ...nextFields,
                    });
                  }
                },
              },
              rule: [
                {
                  validator: (rule, v) => {
                    if (
                      !v ||
                      isNil(v?.value) ||
                      isNil(v?.currency) ||
                      v?.value === ''
                    ) {
                      return Promise.reject(new Error('请填写'));
                    }
                    if (v?.value === 0) {
                      return Promise.reject(new Error('金额不能为0'));
                    }
                    return Promise.resolve();
                  },
                },
              ],
            }),
            render: (v: any) => <CurrencyInput view value={v} />,
          },
        ];

        // 付款方式子表（addGroup：付款类型 + 付款金额 + 操作）
        const typeColumn: AddGroupFormColumn = {
          dataIndex: 'payment_type',
          title: '付款类型',
          width: 170,
          mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id', 'payment_method_key'],
          editRender: ({ row }) => ({
            name: 'select',
            editable: isEdit,
            props: {
              options: getPaymentTypeOptions(row),
              placeholder: '请选择',
              size: 'middle',
            },
            rule: [{ required: true, message: '请选择付款类型' }],
          }),
          render: (v: any) => {
            const name = PAYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.name;
            return <span>{name || v || '--'}</span>;
          },
        };
        const amountColumn: AddGroupFormColumn = {
          dataIndex: 'payment_amount',
          title: '付款金额',
          width: 250,
          mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id', 'payment_method_key'],
          editRender: ({ row }) => ({
            name: 'custom-component',
            editable: isEdit,
            props: {
              customComp: PaymentAmountInput,
              row,
              editable: isEdit,
            },
            rule: [
              {
                required: true,
                message:
                  row?.payment_type === PAYMENT_TYPE_ROYALTY
                    ? '请输入比例'
                    : '请输入付款金额',
              },
            ],
          }),
          render: (v: any, row: InsuranceAmountRow) => (
            <PaymentAmountInput value={v} row={row} editable={false} />
          ),
        };
        const paymentMethodOperationColumn: AddGroupFormColumn = {
          dataIndex: 'payment_method_operation',
          title: '操作',
          width: 60,
          mergeRowSpanKey: [INSURANCE_GROUP_KEY, 'id', 'payment_method_key'],
          render: (__v: any, row: InsuranceAmountRow) => {
            // $addRow 占位行不渲染删除（其操作 cell 已被 addGroup colSpan 隐藏，此处防御）
            if (row?.$addRow) return null;
            return (
              <Button
                danger
                type="text"
                onClick={() =>
                  handleDeletePaymentMethod(row?.payment_method_key)
                }
              >
                删除
              </Button>
            );
          },
        };
        const paymentMethodGroup: AddGroupFormColumn = {
          dataIndex: 'payment_methods_group',
          title: '付款方式',
          addGroup: true,
          addGroupKey: [INSURANCE_GROUP_KEY, 'id'],
          addCellRender: (mainRow: InsuranceAmountRow) =>
            isEdit ? (
              <Button
                type="text"
                icon={<Plus />}
                className="text-[#1677ff]"
                onClick={() => handleAddPaymentMethod(mainRow)}
              >
                新增
              </Button>
            ) : null,
          children: [
            typeColumn,
            amountColumn,
            ...(isEdit ? [paymentMethodOperationColumn] : []),
          ],
        };

        // root addGroup 主表（常量分组键；含主行列 + 付款方式子表）
        const rootColumn: AddGroupFormColumn = {
          dataIndex: 'insurance_amount',
          title: null,
          addGroup: true,
          addGroupKey: [INSURANCE_GROUP_KEY],
          children: [
            ...mainColumns,
            paymentMethodGroup,
            ...(isEdit ? [mainOperationColumn] : []),
          ],
        };
        if (isEdit) {
          rootColumn.addCellRender = () => (
            <Button
              type="text"
              icon={<Plus />}
              className="text-[#1677ff]"
              onClick={handleAddMainRow}
            >
              新增
            </Button>
          );
        }
        // level0 锚点列（nodeIdKey = 常量分组键，使根级 addGroup 有父节点；hideInTable 仅参与树模型）
        return [
          {
            dataIndex: 'id',
            title: null,
            hideInTable: true,
            mergeRowSpanKey: [INSURANCE_GROUP_KEY],
          },
          rootColumn,
        ];
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        isEdit,
        mergedOurPartyOptions,
        otherPartyOptions,
        handleSearch,
        negotiationCurrency,
        negotiationCurrencyName,
        paymentBudgetConfig,
      ]
    );

    return (
      <ProTablePlusForm
        ref={tableRef}
        value={value}
        onDataChange={handleChange as any}
        columns={columns}
        rowKey={(r) => `${r?.id ?? ''}-${r?.payment_method_key ?? ''}`}
        verticalBorder
        scroll={{ x: 'max-content' }}
        readOnly={!isEdit}
      />
    );
  }
);

InsuranceAmountTable.displayName = 'InsuranceAmountTable';

export default InsuranceAmountTable;
